import React from "react";
import { Link } from "react-router-dom";

import { useAccountState } from "../hooks/useAccountState";
import { useCurrentCharacterName } from "../hooks/useCurrentCharacterName";
import type { FeatureKey } from "../lib/api";
import { getCharacterShortName } from "../lib/characterName";
import { canUseFeature, FEATURE_LOCK_MESSAGE } from "../lib/featureAccess";

function EntryCard({
  title,
  description,
  to,
  locked,
  onLockedClick,
}: {
  title: string;
  description: string;
  to: string;
  feature: FeatureKey;
  locked: boolean;
  onLockedClick: () => void;
}) {
  if (locked) {
    return (
      <button
        type="button"
        aria-disabled="true"
        onClick={onLockedClick}
        className="block w-full rounded-[2rem] border border-[#E0D5CE] bg-[#F4EEE9] p-6 text-left shadow-[0_14px_34px_rgba(124,90,78,0.10)] opacity-85"
      >
        <span className="inline-flex rounded-full bg-white px-3 py-1 text-[11px] font-black text-[#A1887D]">
          ロック中
        </span>
        <h2 className="mt-4 text-xl font-black tracking-tight text-[#5D4D46]">{title}</h2>
        <p className="mt-2 text-sm leading-6 text-[#6B5851]">{description}</p>
        <p className="mt-5 text-sm font-black text-[#A67C52]">{FEATURE_LOCK_MESSAGE}</p>
      </button>
    );
  }

  return (
    <Link
      to={to}
      className="block rounded-[2rem] border border-[#EADBC8] bg-white/90 p-6 shadow-[0_14px_34px_rgba(124,90,78,0.12)] transition hover:-translate-y-0.5 hover:bg-white"
    >
      <h2 className="text-xl font-black tracking-tight text-[#5D4D46]">{title}</h2>
      <p className="mt-2 text-sm leading-6 text-[#6B5851]">{description}</p>
      <p className="mt-5 text-sm font-black text-[#A67C52]">進む →</p>
    </Link>
  );
}

export default function TalkEntry() {
  const characterName = useCurrentCharacterName();
  const { accountState, loading: accountStateLoading } = useAccountState();
  const [lockedMessage, setLockedMessage] = React.useState<string | null>(null);
  const givenName = getCharacterShortName(characterName);
  const isLocked = (feature: FeatureKey) =>
    accountStateLoading || !accountState || !canUseFeature(accountState, feature);
  const showLockedMessage = () => setLockedMessage(FEATURE_LOCK_MESSAGE);

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-md">
        <header className="mb-6 flex items-center justify-between">
          <Link to="/home" className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]">
            ←戻る
          </Link>
          <p className="text-sm font-bold tracking-tight text-[#7C5A4E]">{givenName}と話す</p>
          <Link to="/home" className="text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]">
            WizMate
          </Link>
        </header>

        <section className="rounded-[2.4rem] border border-white/80 bg-white/85 p-6 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-7">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#A67C52]">TALK & THINK</p>
            <h1 className="mt-3 text-3xl font-black tracking-tight text-[#7C5A4E]">相談のしかたを選ぶ</h1>
          </div>

          <div className="mt-8 space-y-4">
            {lockedMessage ? (
              <p role="status" className="rounded-xl bg-[#FFF8F1] px-4 py-3 text-sm font-bold text-[#A67C52]">
                {lockedMessage}
              </p>
            ) : null}
            <EntryCard
              title={`${givenName}と話す`}
              description={`${givenName}と1対1で、雑談・相談・思考整理を始めます。気軽な話はこちらから。`}
              to="/chat"
              feature="chat"
              locked={isLocked("chat")}
              onLockedClick={showLockedMessage}
            />
            <EntryCard
              title="AI壁打ちルーム"
              description={`${givenName}とAI専門家メンバーが、悩みを整理して次の一手を一緒に考えます。`}
              to="/talk/group"
              feature="group_talk"
              locked={isLocked("group_talk")}
              onLockedClick={showLockedMessage}
            />
          </div>
        </section>
      </div>
    </main>
  );
}
